import apiClient from '@/lib/api';

export const ATTRIBUTION_DIMENSIONS = [
  'source',
  'referrer',
  'funnel_slug',
  'utm_campaign',
  'utm_source',
  'adset',
  'ad',
  'placement',
  'affiliate',
] as const;

export type AttributionDimension = typeof ATTRIBUTION_DIMENSIONS[number];

export const ATTRIBUTION_LABELS: Record<AttributionDimension, string> = {
  source: 'Source',
  referrer: 'Referrer',
  funnel_slug: 'Funnel',
  utm_campaign: 'Campaign',
  utm_source: 'UTM Source',
  adset: 'Ad Set',
  ad: 'Ad',
  placement: 'Placement',
  affiliate: 'Affiliate',
};

export interface CampaignAttribution {
  source: string | null;
  referrer: string | null;
  funnel_slug: string | null;
  utm_campaign: string | null;
  utm_source: string | null;
  adset: string | null;
  ad: string | null;
  placement: string | null;
  /** The stored affiliate token: a referral ID or an affiliate's code */
  affiliate: string | null;
  /** The affiliate's code, e.g. "cameron" */
  affiliateCode: string | null;
  /** The customer's Affonso referral ID, when that's what is stored */
  affiliateReferralId: string | null;
}

export interface CampaignFlatRow {
  agencyId: string;
  agencyName: string;
  agencySlug: string;
  createdAtUtc: string;
  stripeStatus: string | null;
  hasPaidBefore: boolean;
  cancelAtPeriodEnd: boolean;
  trialEndsAtUtc: string | null;
  planId: number | null;
  planName: string | null;
  /** Internal plan name — matches GHL's roasform_current_plan (e.g. "agency"). */
  currentPlan: string | null;
  planMonthlyPrice: number | null;
  adminEmail: string | null;
  adminName: string | null;
  phone: string | null;
  /** GHL custom field roasform_lifecycle_stage */
  lifecycleStage: string;
  /** GHL custom field roasform_billing_status */
  billingStatus: string;
  /** GHL custom field roasform_business_type (onboarding answer) */
  businessType: string | null;
  /** GHL custom field roasform_heard_from (onboarding answer) */
  heardFrom: string | null;
  /** True only when billingStatus is 'paying' (active & not cancelling). */
  converted: boolean;
  attribution: CampaignAttribution;
}

export interface CampaignGroupedRow {
  groupBy: AttributionDimension;
  groupValue: string | null;
  /** Grouped by affiliate only: the affiliate's code and the referral ID behind groupValue */
  affiliateCode?: string | null;
  affiliateReferralId?: string | null;
  signups: number;
  trialing: number;
  activePaid: number;
  canceled: number;
  converted: number;
  conversionRate: number;
  mrr: number;
}

export interface CampaignMeta {
  mode: 'flat' | 'grouped';
  groupBy?: AttributionDimension;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CampaignFlatResponse {
  data: CampaignFlatRow[];
  meta: CampaignMeta;
}

export interface CampaignGroupedResponse {
  data: CampaignGroupedRow[];
  meta: CampaignMeta;
}

export interface CampaignFilters {
  status?: string;
  planId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  includeUntracked?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

function buildParams(
  filters: CampaignFilters,
  groupBy?: AttributionDimension,
): URLSearchParams {
  const params = new URLSearchParams();
  if (groupBy) params.set('groupBy', groupBy);
  params.set('page', String(filters.page ?? 1));
  params.set('limit', String(filters.limit ?? 25));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortDir) params.set('sortDir', filters.sortDir);
  if (filters.status) params.set('status', filters.status);
  if (filters.planId) params.set('planId', filters.planId);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.search) params.set('search', filters.search);
  if (filters.includeUntracked) params.set('includeUntracked', 'true');
  return params;
}

export const campaignsService = {
  getFlat: async (filters: CampaignFilters = {}): Promise<CampaignFlatResponse> => {
    const params = buildParams(filters);
    const res = await apiClient.get(`/admin/campaigns?${params.toString()}`);
    return res.data as CampaignFlatResponse;
  },

  getGrouped: async (
    groupBy: AttributionDimension,
    filters: CampaignFilters = {},
  ): Promise<CampaignGroupedResponse> => {
    const params = buildParams(filters, groupBy);
    const res = await apiClient.get(`/admin/campaigns?${params.toString()}`);
    return res.data as CampaignGroupedResponse;
  },

  /**
   * Fetch every page of the current filter and stream rows out for CSV export.
   * Caps at 5,000 rows to keep the browser sane.
   */
  exportFlatAll: async (filters: CampaignFilters = {}): Promise<CampaignFlatRow[]> => {
    const all: CampaignFlatRow[] = [];
    const limit = 200;
    let page = 1;
    while (all.length < 5000) {
      const res = await campaignsService.getFlat({ ...filters, page, limit });
      all.push(...res.data);
      if (page >= res.meta.totalPages) break;
      page += 1;
    }
    return all;
  },

  exportGroupedAll: async (
    groupBy: AttributionDimension,
    filters: CampaignFilters = {},
  ): Promise<CampaignGroupedRow[]> => {
    const all: CampaignGroupedRow[] = [];
    const limit = 200;
    let page = 1;
    while (all.length < 5000) {
      const res = await campaignsService.getGrouped(groupBy, { ...filters, page, limit });
      all.push(...res.data);
      if (page >= res.meta.totalPages) break;
      page += 1;
    }
    return all;
  },
};
